import { Box, Card, CardContent, Typography } from "@mui/material";
import { DateTime } from "luxon";

// User-facing strings
const STRINGS = {
  MULTIPLE_SESSIONS: " sessions scheduled for today",
  NEXT_SESSION: "Next session: ",
  NO_SESSIONS: "No sessions scheduled for today 🌟",
  ONE_SESSION: "1 session",
  TODAYS_SCHEDULE: "Today's Schedule",
} as const;

interface MeetingTime {
  id: string;
  beginMeetingTime: string;
  endMeetingTime: string;
}

interface TodayScheduleProps {
  meetingTimes: MeetingTime[];
}

export const TodaySchedule = ({ meetingTimes }: TodayScheduleProps) => {
  // Check if there are any events today
  const today = DateTime.now().startOf("day");
  const tomorrow = today.plus({ days: 1 });
  const todaysEvents = meetingTimes.filter((mt) => {
    const startTime = DateTime.fromISO(mt.beginMeetingTime);
    return startTime >= today && startTime < tomorrow;
  });

  // Find the next upcoming event
  const upcomingEvent = meetingTimes
    .filter((mt) => DateTime.fromISO(mt.beginMeetingTime) > DateTime.now())
    .sort(
      (a, b) => DateTime.fromISO(a.beginMeetingTime).toMillis() - DateTime.fromISO(b.beginMeetingTime).toMillis(),
    )[0];

  return (
    <Card sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography
          variant="h6"
          sx={{ fontSize: "1.25rem", fontWeight: "bold", mb: 2 }}
          data-testid="today-schedule-title"
        >
          {STRINGS.TODAYS_SCHEDULE}
        </Typography>
        {todaysEvents.length > 0 ? (
          <Box>
            <Typography variant="body1" sx={{ mb: 1 }} data-testid="today-schedule-count">
              {todaysEvents.length === 1 ? STRINGS.ONE_SESSION : `${todaysEvents.length}${STRINGS.MULTIPLE_SESSIONS}`}
            </Typography>
            {todaysEvents.map((mt) => (
              <Typography key={mt.id} variant="body2" color="text.secondary" data-testid="meeting-time">
                {DateTime.fromISO(mt.beginMeetingTime).toFormat("h:mm a")} -{" "}
                {DateTime.fromISO(mt.endMeetingTime).toFormat("h:mm a")}
              </Typography>
            ))}
          </Box>
        ) : (
          <Box>
            <Typography variant="body1" sx={{ mb: 1 }} data-testid="no-sessions-message">
              {STRINGS.NO_SESSIONS}
            </Typography>
            {upcomingEvent && (
              <Typography variant="body2" color="text.secondary" data-testid="next-session">
                {STRINGS.NEXT_SESSION}
                {DateTime.fromISO(upcomingEvent.beginMeetingTime).toFormat("cccc, LLL d @ h:mm a")}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
